/**
 * Vesper web-next — Platform Initialization
 *
 * Must be imported BEFORE any shared code that accesses storage or API.
 */

import { initStorage, initApiBase, setOnAuthFailure, useAuthStore, userService } from '@vesper/shared';

declare const process: { env: Record<string, string | undefined> };

/** Resolve the backend API base URL.
 *
 *  Priority:
 *    1. REACT_APP_API_URL env override (production deploys, custom dev setups)
 *    2. LAN-IP auto-derive for dev-on-phone (host:3001 if host is a private
 *       LAN address — covers http://192.168.x.x:3003 from a real phone so
 *       Horizon picks + Map nearbyAll + avatars don't 404 silently)
 *    3. Final fallback: localhost:3001 (SSR / unit-test / odd browsers)
 *
 *  IMPORTANT: In production deploys REACT_APP_API_URL MUST be set in env.
 *  The auto-derive in (2) only triggers for private LAN addresses; a public
 *  hostname without REACT_APP_API_URL set logs an error and falls back to
 *  same-origin /api/v1 (which only works if the backend is co-hosted under
 *  the same domain via a reverse proxy — usually it isn't, so set the env).
 */
function resolveApiBase(): string {
  const envUrl = process.env['REACT_APP_API_URL'];
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3001/api/v1';
    }
    // Private LAN address ranges (10/8, 172.16/12, 192.168/16) — dev-on-phone scenario.
    const isPrivateLan =
      /^10\./.test(host) ||
      /^192\.168\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);
    if (isPrivateLan) {
      return `http://${host}:3001/api/v1`;
    }
    // Public hostname without REACT_APP_API_URL — likely a misconfigured
    // production deploy. Same-origin fallback works only if backend is
    // reverse-proxied under the same domain.
    // eslint-disable-next-line no-console
    console.error(
      '[init] REACT_APP_API_URL is not set in production. Set it to the ' +
      'backend URL (e.g. https://api.vesperfounders.com/api/v1). Falling ' +
      'back to same-origin /api/v1 which only works under a reverse proxy.',
    );
    return `${window.location.origin}/api/v1`;
  }
  return 'http://localhost:3001/api/v1';
}

const API_URL = resolveApiBase();

initStorage(localStorage);
initApiBase(API_URL);
setOnAuthFailure(() => {
  useAuthStore.getState().clearAuth();
  window.location.href = '/login';
});

// Hydrate token, then fetch user profile so user.id is always available
void useAuthStore.getState().hydrate().then(() => {
  const { isAuthenticated, user, token } = useAuthStore.getState();
  if (isAuthenticated && !user && token) {
    void userService.getMe().then((profile) => {
      // Re-set auth with existing token + fetched profile
      useAuthStore.getState().setAuth(token, profile);
    }).catch(() => {
      // Token expired or invalid — force logout
      useAuthStore.getState().clearAuth();
    });
  }
});
